import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useAppDispatch } from '@/store';
import { createChantier, updateChantier } from '@/store/thunks/chantierThunks';
import type { Chantier } from '@/types';
import { Input, Button } from '@/components/ui';

function toDateInputValue(v: unknown): string {
  if (!v) return '';
  if (typeof v === 'string') {
    // Accept both "YYYY-MM-DD" and ISO strings like "2026-03-25T00:00:00.000Z"
    return v.length >= 10 ? v.slice(0, 10) : v;
  }
  try {
    const d = new Date(v as string);
    if (Number.isNaN(d.getTime())) return '';
    return d.toISOString().slice(0, 10);
  } catch {
    return '';
  }
}

const schema = Yup.object({
  code: Yup.string(),
  nom: Yup.string().required('Nom requis'),
  localisation: Yup.string().max(255, 'Localisation trop longue'),
  date_d: Yup.string()
    .nullable()
    .transform((v) => (v === '' ? null : v))
    .test('is-date', 'Date début invalide', (v) => !v || /^\d{4}-\d{2}-\d{2}$/.test(v)),
  date_f: Yup.string()
    .nullable()
    .transform((v) => (v === '' ? null : v))
    .test('is-date', 'Date fin invalide', (v) => !v || /^\d{4}-\d{2}-\d{2}$/.test(v))
    .test('after-start', 'La date fin doit être après la date début', function (end) {
      const start = this.parent?.date_d as string | null | undefined;
      if (!start || !end) return true;
      return new Date(end).getTime() >= new Date(start).getTime();
    }),
});

interface ChantierFormProps {
  chantier?: Chantier;
  onClose: () => void;
  onSuccess: () => void;
}

export default function ChantierForm({ chantier, onClose, onSuccess }: ChantierFormProps) {
  const dispatch = useAppDispatch();

  const formik = useFormik({
    initialValues: {
      code: chantier?.code ?? '',
      nom: chantier?.nom ?? '',
      localisation: chantier?.localisation ?? '',
      date_d: toDateInputValue(chantier?.date_d),
      date_f: toDateInputValue(chantier?.date_f),
      latitude: chantier?.latitude ?? 0,
      longitude: chantier?.longitude ?? 0,
    },
    validationSchema: schema,
    onSubmit: async (values) => {
      const payload = {
        ...values,
        date_d: values.date_d ? new Date(values.date_d).toISOString() : undefined,
        date_f: values.date_f ? new Date(values.date_f).toISOString() : undefined,
        latitude: values.latitude ?? 0,
        longitude: values.longitude ?? 0,
      };
      if (chantier?.id) {
        const res = await dispatch(updateChantier({ ...chantier, ...payload }));
        if (updateChantier.fulfilled.match(res)) onSuccess();
      } else {
        const res = await dispatch(createChantier(payload));
        if (createChantier.fulfilled.match(res)) onSuccess();
      }
    },
  });

  return (
    <form onSubmit={formik.handleSubmit} className="space-y-4">
      <Input label="Code" id="code" {...formik.getFieldProps('code')} />
      <Input
        label="Nom *"
        id="nom"
        {...formik.getFieldProps('nom')}
        error={formik.touched.nom ? formik.errors.nom : undefined}
      />
      <Input
        label="Localisation"
        id="localisation"
        {...formik.getFieldProps('localisation')}
        error={formik.touched.localisation ? (formik.errors.localisation as string | undefined) : undefined}
      />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Input
          label="Date début"
          id="date_d"
          type="date"
          {...formik.getFieldProps('date_d')}
          error={formik.touched.date_d ? (formik.errors.date_d as string | undefined) : undefined}
        />
        <Input
          label="Date fin"
          id="date_f"
          type="date"
          {...formik.getFieldProps('date_f')}
          error={formik.touched.date_f ? (formik.errors.date_f as string | undefined) : undefined}
        />
       
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <Input
        label="Longitude"
        id="longitude"
        type="number"
        {...formik.getFieldProps('longitude')}
        error={formik.touched.longitude ? (formik.errors.longitude as string | undefined) : undefined}
      />
       <Input
          label="Latitude"
          id="latitude"
          type="number"
          {...formik.getFieldProps('latitude')}
          error={formik.touched.latitude ? (formik.errors.latitude as string | undefined) : undefined}
        />
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onClose}>Annuler</Button>
        <Button type="submit" loading={formik.isSubmitting}>Enregistrer</Button>
      </div>
    </form>
  );
}
